import React, { useState } from "react";
import {
  List,
  Card,
  CardMedia,
  Grid,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Delete, DeleteOutline } from "@mui/icons-material";
import NumberSpinner from "../../components/other/NumberSpinner";
import {
  getApparelColorLabel,
  getApparelColorSwatchStyle,
} from "../../config/products/apparelColors";

const CartList = ({
  cart,
  handleRemoveFromCart,
  t,
  userId,
  priceInfo,
  quantitiesByMotifId,
  onQuantityChange,
}) => {
  const [hoveredRemoveId, setHoveredRemoveId] = useState(null);

  return (
    <List>
      {cart.map((item) => {
        const itemPriceInfo = priceInfo?.[item.id];
        const isProductDraft = item.cartItemType === "productDraft";
        const quantity = Number(
          quantitiesByMotifId?.[item.id] ?? item.quantity ?? 10,
        );
        const sizeSummary = isProductDraft
          ? Object.entries(item.sizeQuantities ?? {})
              .filter(([, count]) => Number(count) > 0)
              .map(([size, count]) => `${size}: ${count}`)
              .join(" · ")
          : "";
        const imageUrl = isProductDraft
          ? item.previewUrl || item.productImage
          : item.imageUrl;
        const title = isProductDraft
          ? item.name || item.productTitle
          : item.name;
        const selectedColorLabel = isProductDraft
          ? item.selectedOptions?.Farge ||
            (item.selectedColor
              ? getApparelColorLabel(item.productType, item.selectedColor)
              : "")
          : item.selectedOptions?.Farge;
        const selectedColorSwatchStyle =
          isProductDraft && item.selectedColor
            ? getApparelColorSwatchStyle(item.productType, item.selectedColor)
            : {};

        return (
          <Card
            key={item.id}
            sx={{
              boxShadow: "none",
              borderRadius: 1,
              borderColor: "divider",
              borderStyle: "solid",
              borderWidth: 1,
              marginBottom: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 5, sm: 4, md: 3 }}>
                {isProductDraft ? (
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={title}
                    sx={{
                      objectFit: "contain",
                      height: "100%",
                      borderRadius: 0,
                      backgroundColor: "#f7f7f5",
                      padding: 1,
                    }}
                  />
                ) : (
                  <Link
                    to={`/dashboard/motif/${item.id}`}
                    state={{ from: "checkout" }}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <CardMedia
                      component="img"
                      image={imageUrl}
                      alt={title}
                      sx={{
                        objectFit: "contain",
                        height: "100%",
                        borderRadius: 0,
                        backgroundColor: "#f7f7f5",
                        padding: 1,
                      }}
                    />
                  </Link>
                )}
              </Grid>

              <Grid size={{ xs: 7, sm: 8, md: 9 }}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  height="100%"
                  paddingRight={1}
                  py={0.5}
                >
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      {isProductDraft ? (
                        <Typography variant="h6" fontWeight={500} noWrap>
                          {title}
                        </Typography>
                      ) : (
                        <Link
                          to={`/dashboard/motif/${item.id}`}
                          state={{ from: "checkout" }}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Typography variant="h6" fontWeight={500} noWrap>
                            {title}
                          </Typography>
                        </Link>
                      )}

                      {!isProductDraft ? (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Tooltip
                            title={
                              !item.active
                                ? t("Article.Inactive")
                                : item.reserved
                                  ? t("Article.Reserved")
                                  : ""
                            }
                            arrow
                          >
                            <Typography
                              variant="subtitle2"
                              paddingX={2}
                              color={
                                !item.active || item.reserved
                                  ? "white"
                                  : "text.main"
                              }
                              sx={{
                                fontWeight: "500",
                                backgroundColor: item.reserved
                                  ? "primary.main"
                                  : !item.active
                                    ? "error.main"
                                    : "secondary.main",
                                borderRadius: 1,
                              }}
                            >
                              {item.article_number}
                            </Typography>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.subtle"
                      sx={{ mt: 0.25, lineHeight: 1.35 }}
                    >
                      {isProductDraft
                        ? `${item.productTitle} • ${item.summary || sizeSummary || `${quantity} stk`} • ${item.logoCount ?? 1} fil`
                        : `${item.width} mm x ${item.height} mm • ${parseFloat(
                            (((item.width / 10) * item.height) / 10).toFixed(2),
                          ).toString()} cm²`}
                    </Typography>

                    {isProductDraft && selectedColorLabel ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          mt: 0.5,
                        }}
                      >
                        {item.selectedColor ? (
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              position: "relative",
                              backgroundColor: "background.paper",
                              border: "1px solid rgba(0,0,0,0.24)",
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 1,
                                borderRadius: "50%",
                                overflow: "hidden",
                                clipPath: "circle(50% at 50% 50%)",
                                transform: "translateZ(0)",
                                ...selectedColorSwatchStyle,
                              }}
                            />
                          </Box>
                        ) : null}
                        <Typography variant="caption" color="text.subtle">
                          {selectedColorLabel}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>

                  <Box
                    mt={1}
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {isProductDraft ? (
                      <Typography variant="body2" color="text.subtle">
                        {quantity} stk
                      </Typography>
                    ) : (
                      <NumberSpinner
                        min={10}
                        value={quantity}
                        onChange={(nextValue) => {
                          const clampedQuantity = Math.max(
                            10,
                            Number(nextValue) || 10,
                          );
                          onQuantityChange?.(item.id, clampedQuantity);
                        }}
                        size="small"
                        variant="compact"
                      />
                    )}

                    <Box display={"flex"} alignItems="center" gap={1}>
                      <Box textAlign="right">
                        <Typography variant="body2" fontWeight={500}>
                          {itemPriceInfo?.totalWOLump != null
                            ? `kr ${itemPriceInfo.totalWOLump}`
                            : itemPriceInfo?.totalExVat != null
                              ? `kr ${itemPriceInfo.totalExVat}`
                              : item.pricing?.totalExVat != null
                                ? `kr ${item.pricing.totalExVat}`
                                : t("Checkout.Calculating")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.subtle"
                          lineHeight={1}
                        >
                          {t("Checkout.VATExcluded")}
                        </Typography>
                      </Box>

                      <Tooltip title={t("Checkout.RemoveFromCart")} arrow>
                        <IconButton
                          size="small"
                          aria-label="remove"
                          onMouseEnter={() => setHoveredRemoveId(item.id)}
                          onMouseLeave={() => setHoveredRemoveId(null)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromCart(item.id);
                          }}
                        >
                          {hoveredRemoveId === item.id ? (
                            <Delete fontSize="small" />
                          ) : (
                            <DeleteOutline fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        );
      })}
    </List>
  );
};

export default CartList;
